import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Loader2, X } from "lucide-react";

import { authApi, type TgStartLinkResponse } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ["auth", "2fa-status"],
    queryFn: authApi.get2faStatus,
    refetchInterval: (q) => {
      // Підтягуємо стан при очікуванні привʼязки.
      const data = q.state.data;
      return data && !data.is_telegram_linked ? 3000 : false;
    },
  });

  const [linkInfo, setLinkInfo] = useState<TgStartLinkResponse | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // Зворотний відлік + автоочистка коду.
  useEffect(() => {
    if (!linkInfo) return;
    setSecondsLeft(linkInfo.expires_in_sec);
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s === null) return null;
        if (s <= 1) {
          setLinkInfo(null);
          return null;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [linkInfo]);

  // Якщо привʼязка завершилась — гасимо інструкцію.
  useEffect(() => {
    if (status.data?.is_telegram_linked) setLinkInfo(null);
  }, [status.data?.is_telegram_linked]);

  const startLink = useMutation({
    mutationFn: authApi.startTgLink,
    onSuccess: (data) => setLinkInfo(data),
  });

  const unlink = useMutation({
    mutationFn: authApi.deleteTgLink,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["auth", "2fa-status"] });
      setLinkInfo(null);
    },
  });

  const enable2fa = useMutation({
    mutationFn: authApi.enable2fa,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "2fa-status"] }),
  });

  const disable2fa = useMutation({
    mutationFn: authApi.disable2fa,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "2fa-status"] }),
  });

  const handleDisable2fa = () => {
    if (!window.confirm(t("settings.two_fa.disable_confirm"))) return;
    disable2fa.mutate();
  };

  const handleUnlink = () => {
    if (!window.confirm(t("settings.telegram.unlink_confirm"))) return;
    unlink.mutate();
  };

  const copyCode = async () => {
    if (!linkInfo) return;
    await navigator.clipboard.writeText(linkInfo.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (status.isLoading || !status.data) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("common.loading")}
      </div>
    );
  }

  const s = status.data;

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-semibold text-brand-navy-700">
        {t("settings.title")}
      </h1>

      {/* ---- Language block ---- */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.language.title")}</CardTitle>
          <CardDescription>{t("settings.language.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {(["uk", "ru"] as const).map((lng) => (
              <Button
                key={lng}
                variant={i18n.language.startsWith(lng) ? "default" : "outline"}
                onClick={() => i18n.changeLanguage(lng)}
              >
                {t(`lang.${lng}`)}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ---- Telegram block ---- */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.telegram.title")}</CardTitle>
          <CardDescription>
            {s.is_telegram_linked
              ? s.tg_username
                ? t("settings.telegram.linked", { username: s.tg_username })
                : t("settings.telegram.linked_no_username")
              : t("settings.telegram.not_linked")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {s.is_telegram_linked && !linkInfo && (
            <Button variant="outline" onClick={handleUnlink} disabled={unlink.isPending}>
              <X className="h-4 w-4" />
              {t("settings.telegram.unlink")}
            </Button>
          )}

          {!s.is_telegram_linked && !linkInfo && (
            <Button onClick={() => startLink.mutate()} disabled={startLink.isPending}>
              {t("settings.telegram.start_link")}
            </Button>
          )}

          {linkInfo && (
            <div className="space-y-4">
              <div>
                <Label>{t("settings.telegram.code_label")}</Label>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-2xl font-mono tracking-[0.5em] text-brand-navy-700">
                    {linkInfo.code}
                  </code>
                  <Button variant="outline" size="icon" onClick={copyCode}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                {secondsLeft !== null && (
                  <div className="mt-1 text-xs text-slate-500">
                    {t("settings.telegram.expires_in", { seconds: secondsLeft })}
                  </div>
                )}
              </div>

              <div className="text-sm text-slate-600">
                {t("settings.telegram.code_hint")}{" "}
                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                  /start {linkInfo.code}
                </code>
              </div>

              <a
                href={linkInfo.deeplink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-brand-navy-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy-600"
              >
                <ExternalLink className="h-4 w-4" />
                {t("settings.telegram.open_telegram")}
              </a>

              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("settings.telegram.waiting")}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- 2FA block ---- */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.two_fa.title")}</CardTitle>
          <CardDescription>{t("settings.two_fa.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            {s.is_enabled ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-mint-100 px-3 py-1 text-brand-mint-700">
                <Check className="h-3.5 w-3.5" />
                {t("settings.two_fa.enabled")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-slate-600">
                {t("settings.two_fa.disabled")}
              </span>
            )}
            {s.is_required && (
              <span className="text-xs text-slate-500">
                {t("settings.two_fa.required_by_policy")}
              </span>
            )}
          </div>

          {!s.is_enabled && (
            <Button
              onClick={() => enable2fa.mutate()}
              disabled={!s.is_telegram_linked || enable2fa.isPending}
              title={!s.is_telegram_linked ? t("settings.two_fa.needs_telegram") : undefined}
            >
              {t("settings.two_fa.enable")}
            </Button>
          )}

          {s.is_enabled && !s.is_required && (
            <Button
              variant="outline"
              onClick={handleDisable2fa}
              disabled={disable2fa.isPending}
            >
              {t("settings.two_fa.disable")}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
